import React, { FunctionComponent, useMemo, useRef, useState } from 'react'
import styles from './QuillEditor.module.sass'
import ReactQuill, { Quill } from 'react-quill'
import Button, { ButtonStyle } from '../../controls/Button/Button'
import BoldIcon from 'files/text-bold2.svg'
import ClearFormatIcon from 'files/text-format-clear.svg'
import ColorIcon from 'files/text-color2.svg'
import { UploadData, useBoolean } from '../../../utils/hooks'
import MagicUrl from 'quill-magic-url'
import Popover from '../Popover'
import clsx from 'clsx'
import { Classes, Colors } from '../../../styles'
import CustomArrowContainer from '../popover/ArrowContainer/CustomArrowContainer'
import DoubleSwitch from '../../controls/DoubleSwitch/DoubleSwitch'
import CloseIcon from '../../../../files/close.svg'
import CheckIcon from '../../../../files/check.svg'
import SendIcon from '../../../../files/send.svg'
import AttachIcon from '../../../../files/clip2.svg'
import { AnimatePresence } from 'framer-motion'
import { RequestStatus } from '../../../responses/response'
import { Comment, CommentData } from '../../../responses/comments'
import { isTempFileData } from '../../../responses/files'
import QuoteElement from '../../data-components/elements/QuoteElement/QuoteElement'
import { Portal } from 'react-portal'
import { FileDrop } from 'react-file-drop'
import UploadIcon from '../../../../files/upload.svg'
import FileElement from '../../data-components/files/FileElement/FileElement'
import { shallowSet } from '../../../utils/utils'

interface Props {
    className?: string,
    value?: string | null,
    onChange: (value: string) => void,
    resulted?: boolean,
    onResultedChange?: (value: boolean) => void,
    onSend?: (data: CommentData) => void,
    onCancel?: () => void,
    placeholder?: string,
    editMode?: boolean,
    upload?: UploadData,
    loading?: boolean,
    quote?: Comment | null,
    onQuoteDelete?: () => void,
    rightFiles?: boolean,
}

const formats = ['bold', 'color', 'link', 'clean']
const colors = ['rgb(205, 36, 54)', 'rgb(60, 184, 131)']

Quill.register({
    'modules/magicUrl': MagicUrl,
})

const modules = {
    toolbar: false,
    magicUrl: true,
}

const sanitize = (value: string): string => value.replace(/\t/,'')

const QuillEditor: FunctionComponent<Props> = ({onChange, ...props}) => {
    const value = useMemo(() => props.value ?? '', [props.value])
    const {state, toggle, setFalse} = useBoolean()
    const inputRef = useRef<HTMLInputElement>(null)
    const editorRef = useRef<ReactQuill>(null)

    const [isEmpty, setIsEmpty] = useState(!props.value)
    const [format, setFormat] = useState<Record<string, string | boolean>>({})
    const canSend = !isEmpty || (props.upload?.files.length ?? 0) > 0


    const send = () => {
        props.onSend?.({
            ref: props.quote,
            text: isEmpty? null: sanitize(value),
            files: props.upload?.files.filter(isTempFileData).map(file => file.data),
            resulted: props.resulted,
        })
    }

    return <div
        className={clsx(styles.editor, props.className, {[styles.rightFiles]: props.rightFiles})}
        onPaste={event => props.upload?.upload([...event.clipboardData.files])}
    >
        {
            props.quote && <QuoteElement comment={props.quote} onDelete={props.onQuoteDelete}/>
        }
        {
            props.upload && <Portal>
                <FileDrop
                    onDrop={files => files && props.upload?.upload([...files])}
                    className={styles.dropArea}
                >
                    <UploadIcon/>
                    <div>Отпускайте</div>
                </FileDrop>
            </Portal>
        }
        <ReactQuill
            ref={editorRef}
            className={styles.input}
            placeholder={props.placeholder}
            value={value}
            onChange={(value, delta, source, editor) => {
                onChange(value)
                setIsEmpty(editor.getText().trim().length === 0)
            }}
            modules={modules}
            formats={formats}
            onChangeSelection={(selection) => {
                setFormat(shallowSet(selection && editorRef.current?.getEditor().getFormat(selection) || {}))
            }}
            // onKeyPress={event => {
            //     console.log(event)
            //     if (event.keyCode === 13 && event.ctrlKey) {
            //         send()
            //         event.stopPropagation()
            //         event.preventDefault()
            //     }
            // }}
        />
        <div className={styles.actions}>
            {
                props.upload && <Button
                    title={'Прикрепить файлы'}
                    onClick={() => inputRef.current?.click()}
                >
                    <AttachIcon/>
                </Button>
            }
            <Button active={!!format.bold} onClick={() => editorRef.current?.getEditor().format('bold', !format.bold)}>
                <BoldIcon/>
            </Button>
            <Popover
                reposition={false}
                isOpen={state}
                onClickOutside={setFalse}
                positions={['top']}
                padding={0}
                content={state => <CustomArrowContainer state={state}>
                    <div className={styles.colorPopover}>
                        {
                            colors.map(color => <div
                                key={color}
                                className={clsx(styles.color, Classes.touchTarget)}
                                style={{background: color}}
                                onClick={() => {
                                    editorRef.current?.getEditor().format('color', color === format.color ? '' : color)
                                    setFalse()
                                }}
                            />)
                        }
                    </div>
                </CustomArrowContainer>}
            >
                <Button
                    active={!!format.color}
                    onClick={event => {
                        event.preventDefault() // save focus on field
                        toggle()
                    }}
                >
                    <ColorIcon/>
                </Button>
            </Popover>
            <Button onClick={() => editorRef.current?.getEditor().removeFormat(editorRef.current?.getEditor().getSelection()?.index || 0,editorRef.current?.getEditor().getSelection()?.length || 100000)}>
                <ClearFormatIcon/>
            </Button>
            {
                props.onResultedChange && <DoubleSwitch
                    className={styles.switch}
                    value={props.resulted && ((props.upload?.files.length ?? 0) > 0) ? true : false }
                    onChange={props.onResultedChange}
                    left={'Комментарий'}
                    right={'Результат'}
                    leftColor={Colors.darkBlue}
                    rightColor={Colors.green}
                />
            }
        </div>
        {
            props.upload && <div className={styles.files}>
                <input
                    ref={inputRef}
                    multiple
                    type={'file'}
                    style={{display: 'none'}}
                    onChange={() => {
                        if (inputRef.current) {
                            props.upload?.upload([...inputRef.current.files || []])
                            inputRef.current.value = ''
                        }
                    }}
                />
                <AnimatePresence>
                    {
                        props.upload.files.map(file => <FileElement
                            delete
                            layout
                            key={file.id}
                            file={file.data}
                            onClick={() => {
                                props.upload?.remove(file)
                                }
                            }
                            loading={file.status === RequestStatus.FETCHING}
                        />)
                    }
                </AnimatePresence>
            </div>
        }
        {
            (props.editMode || canSend) && <Button
                className={styles.clear}
                title={props.editMode ? 'Отмена' : 'Очистить'}
                buttonStyle={ButtonStyle.TINTED}
                color={Colors.red}
                onClick={() => {
                    if (props.editMode) {
                        props.onCancel?.()
                    } else {
                        onChange('')
                        props.upload?.clear()
                    }
                }}
            >
                <CloseIcon/>
            </Button>
        }
        {
            props.onSend && <Button
                disabled={props.loading || !canSend || !(props.upload?.isUploaded ?? true)}
                title={props.editMode ? 'Сохранить' : 'Отправить'}
                className={styles.send}
                buttonStyle={ButtonStyle.TINTED}
                color={Colors.blue}
                onClick={send}
            >
                {
                    props.editMode ? <CheckIcon/> : <SendIcon/>
                }
            </Button>
        }
    </div>
}

export default QuillEditor